import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Spinner,
  Badge,
  Pagination,
  InputGroup,
  FormControl
} from 'react-bootstrap';
import { FiEdit2, FiTrash2, FiUserCheck, FiUserX, FiPlus } from 'react-icons/fi';
import { systemsAPI, customersAPI, productsAPI, gatewaysAPI } from '../services/api';
import { System, SystemsResponse, Customer, Product, Gateway } from '../types';
import { useAuth } from '../contexts/AuthContext';

const Systems: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [systems, setSystems] = useState<System[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingSystem, setEditingSystem] = useState<System | null>(null);
  const [selectedSystem, setSelectedSystem] = useState<System | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    systemName: '',
    systemCode: '',
    description: '',
    location: '',
    customerId: '',
    primaryProductId: '',
    gatewayId: '',
    installationDate: new Date().toISOString().split('T')[0]
  });

  // Product form state (extended with optional IoT metadata)
  const [productFormData, setProductFormData] = useState({
    productId: '',
    quantity: 1,
    metadata: {
      companyId: '',
      gateway: '',
      productType: '',
      locationLabel: '',
      itemCode: '',
      itemSerialNumber: '',
      productNameOverride: '',
      label: '',
      specifications: '',
      parameters: [
        { name: '', unit: '', dividingFactor: undefined as number | undefined, systemType: '' }
      ]
    }
  });

  const fetchSystems = useCallback(async () => {
    try {
      setLoading(true);
      const response: SystemsResponse = await systemsAPI.getAll(
        currentPage, 
        10, 
        searchTerm
      );
      setSystems(response.systems);
      setTotalPages(response.pagination.total);
    } catch (error) {
      setError('Failed to fetch systems');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [customersRes, productsRes, gatewaysRes] = await Promise.all([
        customersAPI.getAll(1, 100),
        productsAPI.getAll(1, 100),
        gatewaysAPI.getAll(1, 100)
      ]);
      
      setCustomers(customersRes.customers);
      setProducts(productsRes.products);
      setGateways(gatewaysRes.gateways);
    } catch (error) {
      console.error('Failed to fetch dropdown data:', error);
    }
  }, []);

  useEffect(() => {
    fetchSystems();
    if (currentUser?.role === 'superadmin') {
      fetchDropdownData();
    }
  }, [fetchSystems, fetchDropdownData, currentUser]);

  const resetForm = () => {
    setFormData({
      systemName: '',
      systemCode: '',
      description: '',
      location: '',
      customerId: '',
      primaryProductId: '',
      gatewayId: '',
      installationDate: new Date().toISOString().split('T')[0]
    });
    setEditingSystem(null);
  };

  const handleShowModal = (system?: System) => {
    if (system) {
      setEditingSystem(system);
      setFormData({
        systemName: system.systemName,
        systemCode: system.systemCode,
        description: system.description || '',
        location: system.location || '',
        customerId: typeof system.customerId === 'object' ? system.customerId.id : system.customerId || '',
        primaryProductId: typeof system.primaryProductId === 'object' ? system.primaryProductId.id : system.primaryProductId || '',
        gatewayId: typeof system.gatewayId === 'object' ? system.gatewayId.id : system.gatewayId || '',
        installationDate: new Date(system.installationDate).toISOString().split('T')[0]
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    setError('');
    setSuccess('');
  };

  const handleShowProductModal = (system: System) => {
    setSelectedSystem(system);
    // Pre-fill metadata with known values when possible
    setProductFormData({
      productId: '',
      quantity: 1,
      metadata: {
        companyId: typeof system.customerId === 'object' ? system.customerId.companyName : '',
        gateway: typeof system.gatewayId === 'object' ? system.gatewayId.gatewayName : '',
        productType: '',
        locationLabel: system.location || '',
        itemCode: '',
        itemSerialNumber: '',
        productNameOverride: '',
        label: '',
        specifications: '',
        parameters: [
          { name: '', unit: '', dividingFactor: undefined, systemType: '' }
        ]
      }
    });
    setShowProductModal(true);
  };

  const handleCloseProductModal = () => {
    setShowProductModal(false);
    setSelectedSystem(null);
    setProductFormData({
      productId: '',
      quantity: 1,
      metadata: {
        companyId: '',
        gateway: '',
        productType: '',
        locationLabel: '',
        itemCode: '',
        itemSerialNumber: '',
        productNameOverride: '',
        label: '',
        specifications: '',
        parameters: [
          { name: '', unit: '', dividingFactor: undefined, systemType: '' }
        ]
      }
    });
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const submitData = { ...formData };
      
      // For customers, use their own customer ID
      if (currentUser?.role === 'customer' && currentUser.customerId) {
        submitData.customerId = typeof currentUser.customerId === 'string' 
          ? currentUser.customerId 
          : currentUser.customerId.id;
      }

      if (editingSystem) {
        await systemsAPI.update(editingSystem.id, submitData);
        setSuccess('System updated successfully');
      } else {
        await systemsAPI.create(submitData);
        setSuccess('System created successfully');
      }
      
      fetchSystems();
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (error: any) {
      setError(error.message || 'Operation failed');
    }
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedSystem) return;

    try {
      await systemsAPI.addProduct(selectedSystem.id, productFormData as any);
      setSuccess('Product added to system successfully');
      fetchSystems();
      setTimeout(() => {
        handleCloseProductModal();
      }, 1500);
    } catch (error: any) {
      setError(error.message || 'Failed to add product');
    }
  };

  const handleToggleStatus = async (systemId: string) => {
    try {
      await systemsAPI.toggleStatus(systemId);
      fetchSystems();
    } catch (error) {
      setError('Failed to toggle system status');
    }
  };

  const handleDelete = async (systemId: string) => {
    if (window.confirm('Are you sure you want to delete this system?')) {
      try {
        await systemsAPI.delete(systemId);
        fetchSystems();
      } catch (error) {
        setError('Failed to delete system');
      }
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchSystems();
  };

  const resetFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      online: 'success',
      offline: 'secondary',
      maintenance: 'warning',
      error: 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  // Dynamic parameter controls for Add Product modal
  const addParameter = () => {
    setProductFormData(prev => {
      const next = { ...prev } as any;
      next.metadata.parameters = [
        ...next.metadata.parameters,
        { name: '', unit: '', dividingFactor: undefined, systemType: '' }
      ];
      return next;
    });
  };

  const removeParameter = (idx: number) => {
    setProductFormData(prev => {
      const next = { ...prev } as any;
      next.metadata.parameters = next.metadata.parameters.filter((_: any, i: number) => i !== idx);
      return next;
    });
  };

  if (loading && systems.length === 0) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem', color: '#667eea' }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading systems...</p>
      </Container>
    );
  }

  const canManageSystems = currentUser?.role === 'superadmin' || currentUser?.role === 'customer';

  return (
    <Container fluid className="py-3">
      <div style={{ width: '95%', maxWidth: '1800px', margin: '0 auto' }}>
      <Row className="mb-4">
        <Col>
          <div className="gradient-card p-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <h2 className="mb-2 fw-bold">Systems Management</h2>
              <p className="mb-0 opacity-90">Monitor and manage all system installations</p>
            </div>
            {canManageSystems && (
              <Button variant="primary" onClick={() => handleShowModal()} className="btn-gradient btn-gradient-primary">
                <FiPlus size={16} className="me-2" />Add New System
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Search */}
      <Row className="mb-4">
        <Col>
          <Card className="modern-card">
            <Card.Body>
              <Form onSubmit={handleSearch}>
                <Row className="g-3">
                  <Col md={8}>
                    <InputGroup>
                      <FormControl
                        placeholder="Search systems..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex gap-2">
                      <Button type="submit" variant="outline-primary">
                        Search
                      </Button>
                      <Button type="button" variant="outline-secondary" onClick={resetFilters}>
                        Reset
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')} className="modern-card">{error}</Alert>}

      <Row>
        <Col>
          <Card className="modern-card">
            <Card.Body>
              <div className="table-responsive">
                <Table className="modern-table">
                  <thead>
                    <tr>
                      <th>System Name</th>
                      <th>Client Name</th>
                      <th>Product Name</th>
                      <th>Gateway</th>
                      <th>Total Products</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {systems.map((system) => (
                      <tr key={system.id}>
                        <td>
                          <strong>{system.systemName}</strong>
                          <br />
                          <small className="text-muted">{system.systemCode}</small>
                        </td>
                        <td>
                          {typeof system.customerId === 'object' 
                            ? system.customerId.companyName 
                            : 'N/A'
                          }
                        </td>
                        <td>
                          {typeof system.primaryProductId === 'object' 
                            ? system.primaryProductId.productName 
                            : 'N/A'
                          }
                        </td>
                        <td>
                          {typeof system.gatewayId === 'object' 
                            ? system.gatewayId.gatewayName 
                            : 'N/A'
                          }
                        </td>
                        <td>
                          <Badge bg="info">{system.totalProducts}</Badge>
                          {canManageSystems && (
                            <>
                              <br />
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="mt-1"
                                onClick={() => handleShowProductModal(system)}
                              >
                                Add Product
                              </Button>
                            </>
                          )}
                        </td>
                        <td>
                          <Badge bg={system.isActive ? 'success' : 'secondary'}>
                            {system.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          <br />
                          {getStatusBadge(system.status)}
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {canManageSystems && (
                              <>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleShowModal(system)}
                                  title="Edit system"
                                  aria-label="Edit system"
                                >
                                  <FiEdit2 size={14} />
                                </Button>
                                
                                <Button
                                  variant={system.isActive ? 'outline-warning' : 'outline-success'}
                                  size="sm"
                                  onClick={() => handleToggleStatus(system.id)}
                                >
                                  {system.isActive ? <><FiUserX size={14} className="me-1" />Deactivate</> : <><FiUserCheck size={14} className="me-1" />Activate</>}
                                </Button>
                                
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDelete(system.id)}
                                  title="Delete system"
                                  aria-label="Delete system"
                                >
                                  <FiTrash2 size={14} />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {systems.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">
                    {canManageSystems 
                      ? 'No systems found. Click "Add New System" to create one.'
                      : 'No systems found.'
                    }
                  </p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.Prev 
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    />
                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                      const pageNum = Math.max(1, currentPage - 2) + index;
                      if (pageNum <= totalPages) {
                        return (
                          <Pagination.Item
                            key={pageNum}
                            active={pageNum === currentPage}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </Pagination.Item>
                        );
                      }
                      return null;
                    })}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* System Form Modal */}
      {canManageSystems && (
        <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
          <Modal.Header closeButton className="gradient-card-header border-0">
            <Modal.Title>
              {editingSystem ? 'Edit System' : 'Add New System'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body className="p-4">
              {error && <Alert variant="danger" className="modern-card">{error}</Alert>}
              {success && <Alert variant="success" className="modern-card">{success}</Alert>}
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>System Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="systemName"
                      value={formData.systemName}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>System Code *</Form.Label>
                    <Form.Control
                      type="text"
                      name="systemCode"
                      value={formData.systemCode}
                      onChange={handleInputChange}
                      placeholder="e.g., ADMIN-DWS"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              {currentUser?.role === 'superadmin' && (
                <Form.Group className="mb-3">
                  <Form.Label>Client (Customer) *</Form.Label>
                  <Form.Select
                    name="customerId"
                    value={formData.customerId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Client</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.companyName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Primary Product *</Form.Label>
                    <Form.Select
                      name="primaryProductId"
                      value={formData.primaryProductId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Product</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.productName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Gateway *</Form.Label>
                    <Form.Select
                      name="gatewayId"
                      value={formData.gatewayId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Gateway</option>
                      {gateways.map(gateway => (
                        <option key={gateway.id} value={gateway.id}>
                          {gateway.gatewayName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Location</Form.Label>
                    <Form.Control
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Installation Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="installationDate"
                      value={formData.installationDate}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter system description..."
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className="border-0">
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" className="btn-gradient btn-gradient-primary">
                {editingSystem ? 'Update System' : 'Create System'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}

      {/* Add Product Modal */}
      {canManageSystems && (
        <Modal show={showProductModal} onHide={handleCloseProductModal} centered>
          <Modal.Header closeButton className="gradient-card-header border-0">
            <Modal.Title>Add Product to System</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleAddProduct}>
            <Modal.Body className="p-4">
              {error && <Alert variant="danger" className="modern-card">{error}</Alert>}
              {success && <Alert variant="success" className="modern-card">{success}</Alert>}
              
              {selectedSystem && (
                <Alert variant="info" className="modern-card">
                  Adding product to: <strong>{selectedSystem.systemName}</strong>
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Product *</Form.Label>
                <Form.Select
                  name="productId"
                  value={productFormData.productId}
                  onChange={(e) => setProductFormData(prev => ({ ...prev, productId: e.target.value }))}
                  required
                >
                  <option value="">Select Product</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.productName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  name="quantity"
                  value={productFormData.quantity}
                  onChange={(e) => setProductFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  min="1"
                  required
                />
              </Form.Group>

              {/* Additional IoT/legacy metadata fields */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Company</Form.Label>
                  <Form.Control
                    type="text"
                    value={productFormData.metadata.companyId}
                    onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, companyId: e.target.value } }))}
                    placeholder="Company ID / Name"
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Gateway</Form.Label>
                  <Form.Control
                    type="text"
                    value={productFormData.metadata.gateway}
                    onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, gateway: e.target.value } }))}
                    placeholder="e.g., ATN-008-0023"
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Product Type</Form.Label>
                  <Form.Control
                    type="text"
                    value={productFormData.metadata.productType}
                    onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, productType: e.target.value } }))}
                    placeholder="e.g., TDS"
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Location Label</Form.Label>
                  <Form.Control
                    type="text"
                    value={productFormData.metadata.locationLabel}
                    onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, locationLabel: e.target.value } }))}
                    placeholder="Location"
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Item Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={productFormData.metadata.itemCode}
                    onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, itemCode: e.target.value } }))}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Item Serial No</Form.Label>
                  <Form.Control
                    type="text"
                    value={productFormData.metadata.itemSerialNumber}
                    onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, itemSerialNumber: e.target.value } }))}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Label>Product Name (override)</Form.Label>
                  <Form.Control
                    type="text"
                    value={productFormData.metadata.productNameOverride}
                    onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, productNameOverride: e.target.value } }))}
                    placeholder="Defaults to selected product"
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Label</Form.Label>
                  <Form.Control
                    type="text"
                    value={productFormData.metadata.label}
                    onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, label: e.target.value } }))}
                  />
                </Col>
              </Row>

              {/* Parameter blocks 1-4 */}
              {productFormData.metadata.parameters.map((p, idx) => (
                <Card className="mb-3" key={idx}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Card.Title className="h6 mb-0">Parameter {idx + 1}</Card.Title>
                      {productFormData.metadata.parameters.length > 1 && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeParameter(idx)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Label>Label</Form.Label>
                        <Form.Control
                          type="text"
                          value={p.name || ''}
                          onChange={(e) => setProductFormData(prev => {
                            const next = { ...prev } as any;
                            next.metadata.parameters[idx] = { ...next.metadata.parameters[idx], name: e.target.value };
                            return next;
                          })}
                        />
                      </Col>
                      <Col md={3}>
                        <Form.Label>Unit</Form.Label>
                        <Form.Control
                          type="text"
                          value={p.unit || ''}
                          onChange={(e) => setProductFormData(prev => {
                            const next = { ...prev } as any;
                            next.metadata.parameters[idx] = { ...next.metadata.parameters[idx], unit: e.target.value };
                            return next;
                          })}
                        />
                      </Col>
                      <Col md={3}>
                        <Form.Label>Dividing Factor</Form.Label>
                        <Form.Control
                          type="number"
                          value={p.dividingFactor ?? ''}
                          onChange={(e) => setProductFormData(prev => {
                            const val = e.target.value === '' ? undefined : Number(e.target.value);
                            const next = { ...prev } as any;
                            next.metadata.parameters[idx] = { ...next.metadata.parameters[idx], dividingFactor: val };
                            return next;
                          })}
                          min="0"
                          step="any"
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label>System type</Form.Label>
                        <Form.Control
                          type="text"
                          value={p.systemType || ''}
                          onChange={(e) => setProductFormData(prev => {
                            const next = { ...prev } as any;
                            next.metadata.parameters[idx] = { ...next.metadata.parameters[idx], systemType: e.target.value };
                            return next;
                          })}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}

              <div className="d-flex justify-content-end mb-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={addParameter}
                >
                  + Add Parameter
                </Button>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Specifications</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={productFormData.metadata.specifications}
                  onChange={(e) => setProductFormData(prev => ({ ...prev, metadata: { ...prev.metadata, specifications: e.target.value } }))}
                  placeholder="Enter specifications..."
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className="border-0">
              <Button variant="secondary" onClick={handleCloseProductModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" className="btn-gradient btn-gradient-primary">
                Add Product
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}
      </div>
    </Container>
  );
};

export default Systems;